<!-- AddTextForm.vue -->

<script setup>
import {
  ref,
  watch,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { toTitleCase, unwrapPdfLineBreaks } from "@/utils/stringUtils";
import { buildCoverFromFile } from "@/utils/coverImage";
// A projeção decide se o conteúdo do editor está de fato vazio: HTML pode ter
// parágrafos sem uma letra dentro.
import { toPlainText } from "@/utils/htmlProjection";
// Mesma regra de bloco do PlaintextToHtml do backend: usada para pré-preencher
// o editor com o conteúdo de um arquivo carregado, antes de qualquer salvar.
import { plaintextToEditorHtml } from "@/utils/tiptapEditorConfig";

// Extração de PDF e EPUB: o navegador não lê esses formatos, então quem
// extrai é a API. Só extrai — não cria texto nenhum.
import { extractTextFromFile } from "@/services/textService";

import { useTextStore } from "@/stores/useTextStore";
import { useAuthorStore } from "@/stores/useAuthorStore";
import { useCategoryStore } from "@/stores/useCategoryStore";
import { useTopicStore } from "@/stores/useTopicStore";
import { useUserStore } from "@/stores/useUserStore";
import { useGroupStore } from "@/stores/useGroupStore";
import { usePermissionStore } from "@/stores/usePermissionStore";
import {
  buildManagerPermissionGrants,
  getDefaultAccessMode,
  getPublicModeHint,
} from "@/utils/permissionDefaults";

import AddAuthorForm from "../Author/AddAuthorForm.vue";
import AddCategoryForm from "@/components/Category/AddCategoryForm.vue";
import AddTopicForm from "@/components/Topic/AddTopicForm.vue";
import AddPermissionForm from "../Generics/AddPermissionForm.vue";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import debounce from "lodash.debounce";
import RichTextEditor from "../Generics/RichTextEditor.vue";
import PlanCatalogByName from "@/components/User/PlanCatalogByName.vue";

import AddTextLimitModal from "./AddTextLimitModal.vue";
import TopicCardModal from "@/components/Topic/TopicCardModal.vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

// 🔹 NOVOS IMPORTS: cards de Autor e Gênero
import AuthorCardModal from "@/components/Author/AuthorCardModal.vue";
import CategoryCardModal from "@/components/Category/CategoryCardModal.vue";

// ====================================================================
// DECLARAÇÕES DE ESTADO
// ====================================================================

// Cadastro rápido: uma tela só, sem passos. Título e conteúdo bastam para
// criar; autor, gênero e assuntos ficam na seção recolhida "Adicionar
// detalhes" e podem esperar a edição do texto.
const showDetails = ref(false);

const detailsPanelRef = ref(null);

function toggleDetails() {
  if (isLoading.value || isSubmitting.value) return;
  showDetails.value = !showDetails.value;

  // Aberta, a seção costuma nascer abaixo da dobra do modal.
  if (showDetails.value) {
    nextTick(() =>
      detailsPanelRef.value?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      })
    );
  }
}

// Texto recém-criado. Enquanto existir, o modal mostra a confirmação
// ("Texto adicionado") no lugar do formulário.
const createdText = ref(null);

// O que a confirmação oferece para completar. Cada item leva ao campo
// correspondente na edição, e não a uma tela genérica: quem clicou em "Autor"
// quer preencher o autor.
// Ícones iguais aos da barra lateral (Autores, Gêneros, Assuntos): o chip leva
// ao mesmo assunto que aqueles itens de menu, e vale ler como a mesma coisa.
const CATALOG_FIELDS = [
  { field: "author", label: "Autor", icon: "fa-pen-nib" },
  { field: "category", label: "Gênero", icon: "fa-book-open" },
  { field: "topics", label: "Assuntos", icon: "fa-lightbulb" },
];

function pendingCatalogFields() {
  const missing = [];
  if (!obj.value.authorId) missing.push("author");
  if (!obj.value.categoryId) missing.push("category");
  if (!(obj.value.topicIds || []).length) missing.push("topics");
  return CATALOG_FIELDS.filter((item) => missing.includes(item.field));
}

const searchTerm = ref("");
const currentPage = ref(1);
const pageSize = ref(30);

const authorList = ref([]);
const categoryList = ref([]);
const topicList = ref([]);

// 🔹 ALTERADO: agora são arrays, pois vamos usar multiple+max=1
const selectedAuthor = ref([]);
const selectedCategory = ref([]);
const selectedTopics = ref([]);

const showAddAuthorModal = ref(false);
const newAuthorName = ref("");

const showAddCategoryModal = ref(false);
const newCategoryName = ref("");

const showAddTopicModal = ref(false);
const newTopicName = ref("");

const authorStore = useAuthorStore();
const categoryStore = useCategoryStore();
const topicStore = useTopicStore();
const textStore = useTextStore();
const userStore = useUserStore();
const groupStore = useGroupStore();
const permissionStore = usePermissionStore();

const emit = defineEmits([
  "close",
  "text-added",
  "open-plans",
  // Saída dos itens em aberto: o pai abre a edição do texto criado.
  "complete-details",
]);

const obj = ref({
  textId: 0,
  topicIds: [],
  categoryId: 0,
  authorId: 0,
  title: "",
  subtitle: "",
  synopsis: "",
  source: "",
  public: false,
  pastedContent: "",
  active: true,
  permissions: [],
});

// ── Capa ─────────────────────────────────────────────────────────────────────
// O arquivo escolhido não sobe como veio: `buildCoverFromFile` recorta em 3:4,
// reduz para 400px e converte para WebP no próprio navegador. O que guardamos
// aqui já é o blob final — ver o cabeçalho de utils/coverImage.js para o porquê
// de o redimensionamento não ser no servidor.
const coverBlob = ref(null);
const coverPreview = ref("");
const coverError = ref("");
const isProcessingCover = ref(false);

function releaseCoverPreview() {
  // Object URL não é coletado enquanto não for revogado: sem isto, trocar de
  // imagem cinco vezes retém os cinco blobs até a aba fechar.
  if (coverPreview.value) URL.revokeObjectURL(coverPreview.value);
  coverPreview.value = "";
}

async function onCoverChange(event) {
  const file = event.target.files?.[0];
  // O input é zerado sempre: sem isso, escolher o MESMO arquivo depois de
  // remover não dispara change de novo.
  event.target.value = "";
  if (!file) return;

  coverError.value = "";
  isProcessingCover.value = true;
  try {
    const cover = await buildCoverFromFile(file);
    releaseCoverPreview();
    coverBlob.value = cover.blob;
    coverPreview.value = cover.previewUrl;
  } catch (e) {
    coverError.value = e.message || "Não foi possível processar a imagem.";
  } finally {
    isProcessingCover.value = false;
  }
}

function clearCover() {
  releaseCoverPreview();
  coverBlob.value = null;
  coverError.value = "";
}

onBeforeUnmount(releaseCoverPreview);

// Teto da sinopse. Espelha o StringLength(2000) do TextCreateDto e o tamanho da
// coluna: os três precisam continuar iguais, senão o formulário promete um
// limite que a API recusa.
const SYNOPSIS_MAX_LENGTH = 2000;

const synopsisLength = computed(() => (obj.value.synopsis ?? "").length);

// 90% do teto: cedo o bastante para dar tempo de encurtar, tarde o bastante
// para não incomodar quem escreve um resumo normal.
const synopsisNearLimit = computed(
  () => synopsisLength.value >= SYNOPSIS_MAX_LENGTH * 0.9
);

// 🔹 NOVO: Objeto para mapear erros inline de validação
const fieldErrors = ref({});

const inputMethod = ref("paste");
const fileName = ref("");

/**
 * Altura da caixa do editor, a mesma nos dois modos.
 *
 * Antes cada modo produzia uma altura: no colar ela vinha da cadeia flex e
 * variava com o que mais estivesse na tela; na prévia do arquivo era uma medida
 * própria. Como é o MESMO campo, com duas origens de conteúdo, a caixa passa a
 * ter um tamanho só — e o botão de expandir cobre quem precisa de mais espaço.
 *
 * 14rem é o piso que o `.form-group--editor` já usava; fixá-lo apenas troca
 * "pelo menos isto" por "exatamente isto".
 */
const EDITOR_HEIGHT = "14rem";

// Extensões aceitas na drop zone. O .txt é lido no navegador; as demais
// precisam do servidor para ser extraídas — ver processFiles. O .doc entra
// aqui mesmo sem suporte real: é o servidor que recusa com a mensagem
// instruindo a salvar como .docx (ver ExtractUploadedFileTextAsync no
// backend), em vez do genérico "formato não permitido" daqui.
const ALLOWED_EXTENSIONS = [".txt", ".pdf", ".epub", ".doc", ".docx"];

// Dica exibida no botão. Sem .doc de propósito: ele nunca importa de
// verdade (ver o comentário acima) — anunciá-lo como aceito aqui enganaria
// quem lê a dica antes de tentar.
const IMPORT_HINT = "Formatos aceitos: .txt, .pdf, .epub, .docx";

// Extensões cuja extração passa pelo servidor — o mesmo conjunto aceito por
// ExtractUploadedFileTextAsync no backend (TextsService), .doc incluído só
// para chegar lá e receber a mensagem de recusa específica.
const SERVER_EXTRACTED_EXTENSIONS = [".pdf", ".epub", ".doc", ".docx"];

// Extrair um PDF de livro leva segundos. Sem sinal na tela, parece travamento.
const isExtracting = ref(false);

/** Extensão em curso de extração, em maiúsculas ("PDF"), para o aviso na tela. */
const extractingExtension = ref("");

const titleFocusRef = ref(null);

/** Botão "Concluir" da tela de confirmação, focado quando ela aparece. */
const finishFocusRef = ref(null);
const authorFocusRef = ref(null);
const categoryFocusRef = ref(null);
const topicFocusRef = ref(null);
const fileInputRef = ref(null);

// estado de carregamento do submit
const isSubmitting = ref(false);
const isLoading = ref(true); // true até o onMounted concluir

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

// 🔹 NOVO: expandir/colapsar o editor de conteúdo no step 2 (espelho do AddNoteForm)
const isBodyExpanded = ref(false);
function handleToggleBodyExpand() {
  if (isLoading.value || isSubmitting.value) return;
  isBodyExpanded.value = !isBodyExpanded.value;
}

// ─── MINI-MODAL DE PERMISSÕES (espelho de AddActivityForm.vue) ───────────────
const showPermissionModal = ref(false);
const permissionBackup = ref([]);
const permissionFormKey = ref(0);
const permissionFormRef = ref(null);

// 🔹 NOVO: arrasto próprio do mini-modal, independente do painel principal.
// O v-if recria o DOM a cada abertura, mas o offset vive aqui — daí o reset.
const {
  panelRef: permPanelRef,
  panelStyle: permPanelStyle,
  isDragging: isPermDragging,
  isDraggable: isPermDraggable,
  onHandlePointerDown: onPermHandlePointerDown,
  resetPosition: resetPermPosition,
} = useDraggableModal();

watch(showPermissionModal, (val) => {
  if (val) resetPermPosition();
});

// Helpers
function getTodosGroupId() {
  // Preferência: o "Todos" DO CÍRCULO — grupo mantido pelo sistema com o titular
  // e seus membros ativos. Sem círculo (usuário individual), cai no grupo global
  // da plataforma, como antes.
  const circleDefault = groupStore.groups.find(
    (g) => g.isCircleDefault === true
  );
  if (circleDefault) return circleDefault.groupId ?? null;

  return (
    groupStore.groups.find((g) => g.groupName?.toLowerCase() === "todos")
      ?.groupId ?? null
  );
}

function getPermissionIdByName(name) {
  return (
    permissionStore.permissions.find(
      (p) => p.permissionName?.toLowerCase() === name.toLowerCase()
    )?.permissionId ?? null
  );
}

function buildPublicPermissions() {
  const todosId = getTodosGroupId();
  const creatorId = loggedUser.value?.userId ?? null;
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");

  const perms = [];
  if (todosId && visualizarId && selecionarId) {
    perms.push(
      { entityId: todosId, type: "group", permissionId: visualizarId },
      { entityId: todosId, type: "group", permissionId: selecionarId }
    );
  }
  if (creatorId && visualizarId && selecionarId && editarId) {
    perms.push(
      { entityId: creatorId, type: "user", permissionId: visualizarId },
      { entityId: creatorId, type: "user", permissionId: selecionarId },
      { entityId: creatorId, type: "user", permissionId: editarId }
    );
  }
  // Contexto de membro gerenciado: o titular acompanha o criador em tudo
  // que o membro publica (ver utils/permissionDefaults.js).
  perms.push(
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId })
  );

  return perms;
}

function buildPrivatePermissions() {
  const creatorId = loggedUser.value?.userId ?? null;
  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");
  if (!creatorId || !visualizarId || !selecionarId || !editarId) return [];
  return [
    { entityId: creatorId, type: "user", permissionId: visualizarId },
    { entityId: creatorId, type: "user", permissionId: selecionarId },
    { entityId: creatorId, type: "user", permissionId: editarId },
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId }),
  ];
}

// Molde com que o formulário abre — regra em utils/permissionDefaults.js
// (getDefaultAccessMode): fechado para quem tem círculo (membro ou titular),
// público só para quem não tem.
function buildInitialPermissions() {
  return getDefaultAccessMode() === "private"
    ? buildPrivatePermissions()
    : buildPublicPermissions();
}

function permsMatch(a, b) {
  if (a.length !== b.length) return false;
  const key = (p) => `${p.type}|${p.entityId}|${p.permissionId}`;
  const setA = new Set(a.map(key));
  return b.every((p) => setA.has(key(p)));
}

// currentAccessMode — computed de 3 estados
const currentAccessMode = computed(() => {
  const perms = obj.value.permissions || [];
  if (perms.length === 0) return "public";
  const publicPerms = buildPublicPermissions();
  if (publicPerms.length > 0 && permsMatch(perms, publicPerms)) return "public";
  const privatePerms = buildPrivatePermissions();
  if (privatePerms.length > 0 && permsMatch(perms, privatePerms))
    return "private";
  return "custom";
});

function selectAccessMode(mode) {
  if (isLoading.value || isSubmitting.value) return;
  if (mode === "public") {
    obj.value.permissions = buildPublicPermissions();
  } else if (mode === "private") {
    obj.value.permissions = buildPrivatePermissions();
  } else if (mode === "custom") {
    openPermissionModal();
  }
}

function openPermissionModal() {
  permissionBackup.value = (obj.value.permissions || []).map((p) => ({ ...p }));
  showPermissionModal.value = true;
}

function cancelPermissionModal() {
  obj.value.permissions = (permissionBackup.value || []).map((p) => ({ ...p }));
  showPermissionModal.value = false;
}

function handleConfirmPermissionModal() {
  const child = permissionFormRef.value;
  if (child && typeof child.validateBeforeConfirm === "function") {
    if (!child.validateBeforeConfirm()) return;
  }
  showPermissionModal.value = false;
}
// ─────────────────────────────────────────────────────────────────────────────

// ====================================================================
// MODAIS DE CARD (Topics, Author, Category)
// ====================================================================

// TopicCardModal
const showTopicCardModal = ref(false);
const selectedTopicIdForModal = ref(null);

function openTopicCard(topicOption) {
  if (!topicOption?.value) return;
  selectedTopicIdForModal.value = topicOption.value;
  showTopicCardModal.value = true;
}

function closeTopicCard() {
  showTopicCardModal.value = false;
  selectedTopicIdForModal.value = null;
}

// 🔹 NOVO: AuthorCardModal
const showAuthorCardModal = ref(false);
const selectedAuthorIdForModal = ref(null);

function openAuthorCard(option) {
  if (!option?.value) return;
  selectedAuthorIdForModal.value = option.value;
  showAuthorCardModal.value = true;
}

function closeAuthorCard() {
  showAuthorCardModal.value = false;
  selectedAuthorIdForModal.value = null;
}

// 🔹 NOVO: CategoryCardModal
const showCategoryCardModal = ref(false);
const selectedCategoryIdForModal = ref(null);

function openCategoryCard(option) {
  if (!option?.value) return;
  selectedCategoryIdForModal.value = option.value;
  showCategoryCardModal.value = true;
}

function closeCategoryCard() {
  showCategoryCardModal.value = false;
  selectedCategoryIdForModal.value = null;
}

// Usuário logado
const loggedUser = computed(() => userStore.currentUser);

// Listas para o componente filho de permissões
const userListForChild = computed(() =>
  (userStore.users || []).map((u) => ({
    ...u,

    id: u.userId ?? u.id,
    name:
      u.userName ??
      u.user_name ??
      u.username ??
      u.fullName ??
      u.name ??
      `Usuário #${u.userId ?? u.id}`,

    // ✅ preserva avatar normalizado pela useUserStore
    photoUrl:
      u.photoUrl ??
      u.photo_url ??
      u.photoPath ??
      u.photo_path ??
      u.userPhotoPath ??
      u.user_photo_path ??
      null,

    // ✅ mantém aliases para o AddPermissionForm/PermissionView conseguirem resolver
    photoPath: u.photoPath ?? u.photo_path ?? u.photoUrl ?? u.photo_url ?? null,

    userPhotoPath:
      u.userPhotoPath ??
      u.user_photo_path ??
      u.photoUrl ??
      u.photo_url ??
      u.photoPath ??
      u.photo_path ??
      null,
  }))
);
const groupListForChild = computed(() =>
  (groupStore.groups || []).map((g) => ({
    id: g.groupId,
    name: g.groupName,
    createdByUserId: g.CreatedByUserId || g.createdByUserId,
    // Membros do grupo: o AddPermissionForm usa para avisar quando a linha de
    // um usuário substitui a permissão que ele teria pelo grupo (precedência).
    members: g.users ?? g.Users ?? [],
  }))
);
const permissionListForChild = computed(() =>
  (permissionStore.permissions || []).map((p) => ({
    id: p.permissionId,
    name: p.permissionName,
  }))
);

// ====================================================================
// SYS BADGE
// ====================================================================

function isSysEntity(entityLike) {
  const name =
    entityLike?.createdByUserName ||
    entityLike?.CreatedByUserName ||
    entityLike?.createdByUser ||
    entityLike?.CreatedByUser;
  return typeof name === "string" && name.toUpperCase() === "SYS";
}

function toOptionWithSys({ label, value, isSys = false, ...rest }) {
  return { label, value, isSys: !!isSys, ...rest };
}

// ====================================================================
// TOAST
// ====================================================================

const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

// Criar não fecha mais o modal: quem confirma é a tela de "Texto adicionado",
// com a saída para completar a catalogação. Daí não haver mais toast de sucesso
// nem esconder o painel por baixo dele.

const TOAST_MS = 2000;

/**
 * Erro fica o TRIPLO do tempo — 6s contra os 2s do sucesso.
 *
 * Não é preferência: aviso de erro costuma ser uma instrução de várias linhas,
 * não uma confirmação. "Envie um PDF com texto selecionável, faça OCR antes"
 * precisa ser lido inteiro para servir de alguma coisa, e um aviso que some
 * antes de ser lido não avisou nada. Sucesso segue curto: ali a mensagem
 * confirma o que o usuário acabou de fazer, e ele já sabe o que diz.
 */
const TOAST_ERROR_MS = TOAST_MS * 3;

function resetToastTimeout(durationMs = TOAST_MS) {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, durationMs);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(TOAST_ERROR_MS);
}

// ====================================================================
// MODAL DE LIMITE DO PLANO
// ====================================================================

const showTextLimitModal = ref(false);
const textLimitInfo = ref({
  currentUsage: null,
  limit: null,
  newUsage: null,
  message: "",
});

const showPlanCatalogModal = ref(false);

function openPlansFromLimitModal() {
  showTextLimitModal.value = false;
  showPlanCatalogModal.value = true;
}

function handlePlanSelect(payload) {
  console.log("Plano selecionado no catálogo:", payload);
  showPlanCatalogModal.value = false;
}

function handlePlanCatalogClose() {
  showPlanCatalogModal.value = false;
}

// ====================================================================
// FUNÇÕES
// ====================================================================

// ── Visibilidade condensada ("Visibilidade: Público ▾") ─────────────────────
// Mesmos três modos de sempre, resolvidos por currentAccessMode /
// selectAccessMode; aqui só muda a apresentação, que num cadastro rápido não
// pode competir com o conteúdo.
const VISIBILITY_OPTIONS = [
  {
    value: "public",
    label: "Público",
    get hint() {
      return getPublicModeHint();
    },
    icon: "fa-globe-americas",
    colorClass: "visibility--public",
  },
  {
    value: "private",
    label: "Privado",
    hint: "Somente você",
    icon: "fa-lock",
    colorClass: "visibility--private",
  },
  {
    value: "custom",
    label: "Personalizado",
    hint: "Pessoas e grupos que você escolher",
    icon: "fa-user-shield",
    colorClass: "visibility--custom",
  },
];

function visibilityMeta(mode) {
  return (
    VISIBILITY_OPTIONS.find((o) => o.value === mode) ?? VISIBILITY_OPTIONS[0]
  );
}

const isVisibilityMenuOpen = ref(false);
const visibilityDropdownRef = ref(null);

function toggleVisibilityMenu() {
  if (isLoading.value || isSubmitting.value) return;
  isVisibilityMenuOpen.value = !isVisibilityMenuOpen.value;

  // O painel abre para BAIXO e o .modal-body rola: perto do fim da rolagem ele
  // nasceria fora da parte visível. Rolar o gatilho para dentro da vista traz o
  // painel junto, porque ele é filho posicionado do gatilho.
  if (!isVisibilityMenuOpen.value) return;
  nextTick(() => {
    visibilityDropdownRef.value?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  });
}

function handleVisibilityOption(mode) {
  isVisibilityMenuOpen.value = false;
  selectAccessMode(mode);
}

function handleVisibilityClickOutside(event) {
  if (
    isVisibilityMenuOpen.value &&
    !event.target.closest(".visibility-dropdown")
  ) {
    isVisibilityMenuOpen.value = false;
  }
}

function loadAuthorList() {
  authorList.value = authorStore.authors.map((author) =>
    toOptionWithSys({
      label: author.authorName,
      value: author.authorId,
      isSys: isSysEntity(author),
    })
  );
}

async function fetchAuthorsPaged() {
  try {
    await authorStore.fetchAuthorsPaged(
      currentPage.value,
      pageSize.value,
      searchTerm.value
    );
    loadAuthorList();
  } catch (error) {
    console.error("❌ Erro ao buscar autores:", error.message);
  }
}

const onSearch = debounce(async (searchText) => {
  searchTerm.value = searchText;
  currentPage.value = 1;
  await fetchAuthorsPaged();
}, 400);

function addNewAuthor(newTag) {
  newAuthorName.value = toTitleCase(newTag);
  showAddAuthorModal.value = true;
}

function handleAuthorAdded(newAuthor) {
  const normalized = toOptionWithSys({
    label: newAuthor.label,
    value: newAuthor.value,
    isSys: newAuthor.isSys || false,
  });

  authorList.value.push(normalized);

  // 🔹 ALTERADO: selectedAuthor agora é array (max=1)
  selectedAuthor.value = [normalized];
  obj.value.authorId = normalized.value;

  showAddAuthorModal.value = false;
  fetchAuthorsPaged();
}

function addNewCategory(newTag) {
  newCategoryName.value = toTitleCase(newTag);
  showAddCategoryModal.value = true;
}

function handleCategoryAdded(option) {
  const normalized = toOptionWithSys({
    label: option.label,
    value: option.value,
    isSys: option.isSys || false,
  });

  categoryList.value.push(normalized);

  // 🔹 ALTERADO: selectedCategory agora é array (max=1)
  selectedCategory.value = [normalized];
  obj.value.categoryId = normalized.value;

  showAddCategoryModal.value = false;
}

function addNewTopic(newTag) {
  newTopicName.value = toTitleCase(newTag);
  showAddTopicModal.value = true;
}

function handleTopicAdded(option) {
  const normalized = toOptionWithSys({
    label: option.label,
    value: option.value,
    description: option.description,
    isSys: option.isSys || false,
  });

  topicList.value.push(normalized);
  selectedTopics.value.push(normalized);

  obj.value.topicIds = selectedTopics.value.map((t) => t.value);

  showAddTopicModal.value = false;
  focusTopicMultiselect();
}

async function cadastrar() {
  // Salvar no meio de uma extração gravaria o editor ainda vazio.
  if (isSubmitting.value || isExtracting.value) return;

  // 🔹 Resetando os erros inline a cada nova submissão
  fieldErrors.value = {};

  // Título e conteúdo são os dois únicos obrigatórios do cadastro rápido — o
  // resto da catalogação o backend aceita vazio (ver TextCreateDto).
  if (!(obj.value.title || "").trim()) {
    fieldErrors.value.title = "Por favor, informe o título do texto.";
    nextTick(() => titleFocusRef.value?.focus());
    return;
  }

  // Permissões são gerenciadas pelo mini-modal
  const isFileMode = inputMethod.value === "file";

  // No modo arquivo, carregar já preenche obj.value.pastedContent (ver
  // processFiles) — falta só garantir que algo foi carregado antes de cair na
  // checagem de conteúdo visível, comum aos dois modos.
  if (isFileMode && !fileName.value) {
    fieldErrors.value.file = "Por favor, escolha um arquivo para carregar.";
    return;
  }

  // O conteúdo vem do editor rich text e É HTML — tanto o que o usuário
  // colou/digitou quanto o que veio de um arquivo carregado (convertido ao
  // carregar, para o usuário ver e poder editar antes de salvar). Antes
  // passava por stripHtml aqui — o que hoje destruiria justamente a
  // formatação que o usuário acabou de aplicar.
  //
  // A validação de "está vazio?" usa a PROJEÇÃO: um documento como
  // "<p></p><p></p>" tem HTML mas nenhum conteúdo, e precisa ser recusado.
  const html = obj.value.pastedContent || "";
  const visibleText = html ? toPlainText(html).text : "";

  if (!visibleText.trim()) {
    fieldErrors.value[isFileMode ? "file" : "pastedContent"] = isFileMode
      ? "O arquivo carregado está vazio. Escolha outro arquivo ou edite o conteúdo."
      : "Por favor, cole algum conteúdo no editor ou anexe o arquivo do texto.";
    return;
  }

  const dataToSend = {
    topicIds: obj.value.topicIds,
    categoryId: obj.value.categoryId,
    authorId: obj.value.authorId,
    title: obj.value.title,
    subtitle: obj.value.subtitle,
    synopsis: obj.value.synopsis,
    // Blob, nao File: o textService o anexa ao multipart como `cover`.
    coverBlob: coverBlob.value,
    source: obj.value.source,
    active: obj.value.active,
    permissions: obj.value.permissions ?? [],
  };

  dataToSend.pastedContent = html;
  // O backend normaliza na entrada e o texto nasce com content_format = 'html'.
  dataToSend.pastedContentIsHtml = true;

  try {
    isSubmitting.value = true;

    const created = await textStore.createText(dataToSend);

    // O modal continua aberto na confirmação: daqui o usuário escolhe entre
    // seguir em frente e completar a catalogação agora.
    createdText.value = {
      textId: created?.textId ?? created?.TextId ?? null,
      title: dataToSend.title,
      pending: pendingCatalogFields(),
    };
    isSubmitting.value = false;

    // "Concluir" recebe o foco: é a única ação do rodapé nesta tela, e quem
    // acabou de salvar com o teclado espera fechar com Enter, sem procurar o
    // botão. Os itens em aberto acima continuam alcançáveis por Tab.
    nextTick(() => finishFocusRef.value?.focus());
  } catch (error) {
    let isLimitError = false;
    let hasApiFieldErrors = false;

    const data = error.response?.data;

    // Trata validações estruturadas da API para exibir via inline
    // Trata validações estruturadas da API para exibir via inline
    if (data?.errors && typeof data.errors === "object") {
      for (const field in data.errors) {
        const fieldErrorsArray = data.errors[field];
        if (Array.isArray(fieldErrorsArray) && fieldErrorsArray.length > 0) {
          let frontendField = field.charAt(0).toLowerCase() + field.slice(1);

          // 🔹 AJUSTE: Mapeia o "topicsId" do C# para o "topicIds" do Vue
          if (frontendField === "topicsId") {
            frontendField = "topicIds";
          }

          fieldErrors.value[frontendField] = fieldErrorsArray[0];
          hasApiFieldErrors = true;
        }
      }
    }

    if (hasApiFieldErrors) {
      isSubmitting.value = false;
      // Erro num campo que mora na seção recolhida: abre a seção, senão a
      // mensagem inline ficaria escondida.
      if (
        fieldErrors.value.subtitle ||
        fieldErrors.value.synopsis ||
        fieldErrors.value.source ||
        fieldErrors.value.authorId ||
        fieldErrors.value.categoryId ||
        fieldErrors.value.topicIds
      ) {
        showDetails.value = true;
      }
      return; // Interrompe para exibir os erros inline, sem toast
    }

    // Tratamento de erros genéricos ou de limite (que continuam usando toast/modal)
    let userFriendlyMessage = null;
    if (typeof data?.message === "string") {
      userFriendlyMessage = data.message;
    } else if (typeof data === "string") {
      userFriendlyMessage = data;
    } else if (error.message) {
      userFriendlyMessage = error.message;
    }

    if (!userFriendlyMessage) {
      userFriendlyMessage = "Ocorreu um erro desconhecido ao cadastrar.";
    }

    console.error("Erro detalhado da API:", data || error);

    if (
      typeof userFriendlyMessage === "string" &&
      userFriendlyMessage.includes("Seu plano atual permite")
    ) {
      isLimitError = true;
      textLimitInfo.value = {
        currentUsage: null,
        limit: null,
        newUsage: null,
        message: userFriendlyMessage,
      };
    }

    if (isLimitError) {
      isSubmitting.value = false;
      showTextLimitModal.value = true;
    } else {
      showErrorToast(userFriendlyMessage);
      isSubmitting.value = false;
    }
  }
}

function handleFileChange(event) {
  const files = event.target.files;
  if (files && files.length > 0) processFiles(files);
}


async function processFiles(files) {
  const file = files[0];
  const fileExtension = file.name
    .slice(file.name.lastIndexOf("."))
    .toLowerCase();

  if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
    showErrorToast("Formato de arquivo não permitido: " + file.name);
    fileName.value = "";
    return;
  }

  const isServerExtracted = SERVER_EXTRACTED_EXTENSIONS.includes(fileExtension);
  if (isServerExtracted) {
    // Guardado à parte de `fileName`, que só é preenchido no SUCESSO: durante a
    // extração ele ainda está vazio (ou com o arquivo anterior), e o aviso
    // precisa dizer de qual formato se trata agora.
    extractingExtension.value = fileExtension.replace(".", "").toUpperCase();
    isExtracting.value = true;
  }

  try {
    let html;

    if (isServerExtracted) {
      // Nenhum desses o navegador lê sozinho: quem extrai é o servidor, e os
      // três devolvem HTML canônico (já normalizado) — vai direto para o
      // editor, sem reconverter. EPUB e DOCX já SÃO HTML na origem; o PDF não
      // é, mas o PdfHtmlExtractor lê do próprio arquivo a estrutura que o texto
      // puro não carrega (título pelo corpo da fonte, ênfase pela face, lista
      // pelo marcador, alinhamento e recuo pela posição na página). Passá-lo
      // por PlaintextToHtml aqui destruiria justamente essa estrutura.
      // .doc não é suportado — o backend recusa com a mensagem que a API
      // devolve (ver o catch abaixo), instruindo a salvar como .docx.
      const { text, isHtml } = (await extractTextFromFile(file)).data;
      html = isHtml
        ? text
        : plaintextToEditorHtml(unwrapPdfLineBreaks(text));
    } else {
      // Mesmo tratamento de quebra de linha "dura" de PDF que o editor já
      // aplica ao colar (unwrapPastedLineBreaks) — aqui aplicado manualmente,
      // já que carregar um arquivo não passa por um evento de colagem.
      html = plaintextToEditorHtml(unwrapPdfLineBreaks(await file.text()));
    }

    // Preenche o editor com o conteúdo do arquivo — o usuário vê e pode
    // editar antes de salvar.
    obj.value.pastedContent = html;
    fileName.value = file.name;
    fieldErrors.value.file = null;
    fieldErrors.value.pastedContent = null;
  } catch (e) {
    console.warn("Falha ao carregar o conteúdo do arquivo:", e);
    // A API tem o que dizer em vários casos — PDF sem OCR, arquivo grande
    // demais — e a mensagem dela é mais útil que a genérica.
    showErrorToast(
      e?.response?.data?.message ||
        "Não foi possível ler o conteúdo do arquivo selecionado."
    );
    fileName.value = "";
    if (fileInputRef.value) fileInputRef.value.value = "";
  } finally {
    isExtracting.value = false;
  }
}

function closeModal() {
  emit("close");
}

// "Concluir": a lista só é recarregada aqui, quando o modal de fato sai de cena.
function finishAndClose() {
  emit("text-added");
  emit("close");
}

// Um item em aberto entrega o texto recém-criado para o pai abrir a edição, com
// o campo escolhido junto — lá a edição começa com o cursor nele.
function completeDetails(field) {
  if (!createdText.value?.textId) {
    finishAndClose();
    return;
  }
  emit("complete-details", {
    textId: createdText.value.textId,
    focus: typeof field === "string" ? field : null,
  });
}

function setInputMethod(method) {
  inputMethod.value = method;
  fieldErrors.value.file = null;
  fieldErrors.value.pastedContent = null;

  if (method === "file") {
    // O editor só aparece depois que um arquivo é carregado — ver o
    // v-if="fileName" no template. Garante o colapso do estado de expansão.
    isBodyExpanded.value = false;

    // Escolher "Importar arquivo" JÁ abre o seletor: sem a zona de arrasto, o
    // botão é a ação inteira, e obrigar a um segundo clique numa tela que não
    // oferece mais nada seria só uma etapa a pedir.
    //
    // SEMPRE, inclusive com arquivo já carregado — é assim que se troca de
    // arquivo agora. O link "Trocar arquivo" que fazia esse papel saiu: o botão
    // do segmento já é o lugar óbvio para voltar, e ter os dois era oferecer
    // duas portas para a mesma sala.
    nextTick(() => openFilePicker());
  } else {
    // Voltar para "Colar Texto" DESFAZ a importação: o conteúdo na caixa era o
    // do arquivo, e mantê-lo aqui seria oferecer para edição um texto que o
    // usuário acabou de dispensar — sem nome de arquivo na tela dizendo de onde
    // veio, já que `fileName` some logo abaixo.
    //
    // Só quando havia arquivo carregado, e é por isso que o `fileName` é lido
    // ANTES de ser limpo. Sem esse guarda, o caminho do cancelamento
    // (handleFilePickerCancel → setInputMethod("paste")) apagaria o que a pessoa
    // tinha DIGITADO antes de abrir o seletor — desistir de importar não pode
    // custar o texto colado.
    if (fileName.value) obj.value.pastedContent = "";

    fileName.value = "";
    if (fileInputRef.value) fileInputRef.value.value = "";
  }
}

/**
 * Abre o seletor nativo de arquivos.
 *
 * O `<input type="file">` fica escondido porque a caixa que o navegador desenha
 * não combina com o resto do formulário; quem dá a aparência é o botão que
 * chama daqui.
 */
function openFilePicker() {
  if (isExtracting.value) return;
  fileInputRef.value?.click();
}

/**
 * Seletor fechado sem escolher nada.
 *
 * Sem arquivo, o modo arquivo não tem o que mostrar — e como abrir o seletor é
 * automático ao entrar nele, cancelar deixava o usuário num beco: modo aceso,
 * editor vazio, nada carregado. O modo arquivo só permanece depois que um
 * arquivo entra de fato.
 *
 * O guarda `!fileName` cobre a TROCA de arquivo: clicar em "Importar" com um já
 * carregado reabre o seletor, e desistir ali tem de manter o que estava — não
 * jogar o usuário de volta ao modo colar.
 *
 * O evento `cancel` do `<input type="file">` é a API própria para isto. Onde ele
 * não existir, nada acontece — o usuário continua no modo arquivo e clica de
 * novo em "Importar", que é degradação aceitável, não quebra.
 */
function handleFilePickerCancel() {
  if (!fileName.value) setInputMethod("paste");
}

function focusTopicMultiselect() {
  nextTick(() => {
    requestAnimationFrame(() => {
      const root = topicFocusRef.value?.$el;
      const input =
        root?.querySelector("input") ||
        root?.querySelector(".multiselect__input");

      input?.focus();
    });
  });
}

const handleEnterKey = (event) => {
  if (event.key !== "Enter") return;

  // Na confirmação não há mais nada para submeter.
  if (createdText.value) return;

  if (
    showAddAuthorModal.value ||
    showAddCategoryModal.value ||
    showAddTopicModal.value ||
    showPlanCatalogModal.value ||
    showTextLimitModal.value ||
    showTopicCardModal.value ||
    showAuthorCardModal.value ||
    showCategoryCardModal.value ||
    showPermissionModal.value
  ) {
    return;
  }

  const el = event.target;
  const tag = (el?.tagName || "").toLowerCase();
  const isEditable = !!el?.isContentEditable;

  if (tag === "textarea" || isEditable) return;
  if (tag === "button" || tag === "a") return;

  // Dentro de um Multiselect o Enter é DELE: seleciona a opção destacada ou
  // cria o item novo ("tecle enter para criar"). O campo de busca é um <input>
  // comum, então sem esta guarda ele caía na regra geral acima e o Enter
  // salvava o formulário em vez de criar o autor, gênero ou assunto digitado.
  if (typeof el?.closest === "function" && el.closest(".multiselect")) return;

  event.preventDefault();
  cadastrar();
};

// 🔹 FUNÇÃO AUXILIAR: primeiro item (array ou objeto)
function firstOption(val) {
  if (Array.isArray(val)) {
    return val.length > 0 ? val[0] : null;
  }
  return val || null;
}

watch(selectedAuthor, (newVal) => {
  // Seleção única: ao escolher um novo autor, substitui o anterior automaticamente
  if (Array.isArray(newVal) && newVal.length > 1) {
    selectedAuthor.value = [newVal[newVal.length - 1]];
    return;
  }
  fieldErrors.value.authorId = null; // Limpa o erro ao editar
  const first = firstOption(newVal);
  obj.value.authorId = first ? first.value : 0;
});

watch(selectedCategory, (newVal) => {
  // Seleção única: ao escolher um novo gênero, substitui o anterior automaticamente
  if (Array.isArray(newVal) && newVal.length > 1) {
    selectedCategory.value = [newVal[newVal.length - 1]];
    return;
  }
  fieldErrors.value.categoryId = null; // Limpa o erro ao editar
  const first = firstOption(newVal);
  obj.value.categoryId = first ? first.value : 0;
});

watch(selectedTopics, (newVal) => {
  fieldErrors.value.topicIds = null; // Limpa o erro ao editar
  obj.value.topicIds = Array.isArray(newVal) ? newVal.map((x) => x.value) : [];
});

onMounted(async () => {
  isLoading.value = true;

  await Promise.all([
    userStore.fetchUsers(1, 1000, ""),
    groupStore.fetchGroups(1, 1000, ""),
    permissionStore.fetchAllPermissions(),
    authorStore.fetchAuthors(),
    categoryStore.fetchCategories(),
    topicStore.fetchTopics(),
  ]);

  // 🔎 DEBUG: verificar avatares que serão enviados ao AddPermissionForm
  console.group("🖼️ [AddTextForm] userListForChild → AddPermissionForm");
  console.log("Total de usuários:", userListForChild.value.length);
  console.table(
    userListForChild.value.map((u) => ({
      id: u.id,
      name: u.name,
      photoUrl: u.photoUrl,
      photoPath: u.photoPath,
      userPhotoPath: u.userPhotoPath,
    }))
  );
  console.log("Primeiro usuário (raw):", userListForChild.value[0]);
  console.log(
    "api.defaults.baseURL:",
    (await import("@/services/api")).default.defaults.baseURL
  );
  console.groupEnd();
  /////// FIM DEBUG /////////

  loadAuthorList();

  categoryList.value = categoryStore.categories.map((cat) =>
    toOptionWithSys({
      label: cat.categoryName,
      value: cat.categoryId,
      isSys: isSysEntity(cat),
    })
  );

  topicList.value = topicStore.topics.map((form) =>
    toOptionWithSys({
      label: form.topicName,
      value: form.topicId,
      description: form.description,
      isSys: isSysEntity(form),
    })
  );

  // PERMISSÕES INICIAIS via helper (grupo Todos + criador; membro gerenciado
  // abre no molde privado, com o titular junto)
  obj.value.permissions = buildInitialPermissions();

  isLoading.value = false;

  nextTick(() => titleFocusRef.value?.focus());

  document.addEventListener("keydown", handleEnterKey);
  document.addEventListener("click", handleVisibilityClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEnterKey);
  document.removeEventListener("click", handleVisibilityClickOutside);

  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
  }
});
</script>

<template>
  <div class="modal-backdrop">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <div class="modal-header-text">
          <h3 class="modal-title">Adicionar Novo Texto</h3>
          <p v-if="!createdText" class="modal-step-indicator">
            Título e conteúdo bastam, o resto pode ficar para depois
          </p>
        </div>
        <button @click="closeModal" type="button" class="modal-close-btn">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="cadastrar" class="modal-form">
        <div
          class="modal-body"
          :class="{ 'modal-body--expanded': isBodyExpanded }"
        >
          <!-- ✅ Overlay de carregamento -->
          <transition name="loading-fade">
            <div
              v-if="isLoading"
              class="loading-overlay"
              aria-label="Carregando..."
            >
              <svg class="loading-spinner" viewBox="0 0 24 24" fill="none">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              <span class="loading-text">Carregando...</span>
            </div>
          </transition>

          <!-- Cadastro rápido: uma tela só. Título e conteúdo bastam para criar;
               o resto da catalogação vive na seção recolhida "Adicionar
               detalhes" e pode esperar a edição do texto. -->
          <div
            v-if="!createdText"
            class="step-content"
            :class="{ 'step-content--expanded': isBodyExpanded }"
          >
            <!-- 1. Título — o único campo de texto obrigatório -->
            <div class="form-group" v-show="!isBodyExpanded">
              <label for="title" class="form-label">Título</label>
              <input
                ref="titleFocusRef"
                v-model="obj.title"
                @input="fieldErrors.title = null"
                @blur="obj.title = toTitleCase(obj.title)"
                type="text"
                id="title"
                class="input-base"
                :class="{ 'input-error': fieldErrors.title }"
                :disabled="isLoading"
                placeholder="Digite o título do texto"
                required
              />
              <span v-if="fieldErrors.title" class="error-msg">{{
                fieldErrors.title
              }}</span>
            </div>

            <!-- 2. Conteúdo — a ação principal: colar ou carregar -->
            <div class="form-group" v-show="!isBodyExpanded">
              <!-- Dois campos lado a lado: cada um com seu rótulo em cima e seu
                   controle embaixo. Os rótulos dividem uma linha e os controles
                   dividem a seguinte, e as duas colunas se alinham porque a da
                   direita tem largura fixa (--visibility-col). -->
              <div class="content-label-row">
                <!-- "Forma de entrada", e não "Conteúdo": este rótulo governa os
                     botões de método, não a caixa de texto. Quem rotula o
                     conteúdo é a linha logo acima do editor. -->
                <label class="form-label">Forma de entrada</label>
                <span class="form-label visibility-col" id="visibilityLabel"
                  >Visibilidade</span
                >
              </div>

              <div class="content-method-row">
                <div class="access-segment access-segment--sm">
                  <button
                    type="button"
                    class="segment-btn segment-btn--neutral"
                    :class="{ 'segment-btn--active': inputMethod === 'paste' }"
                    :disabled="isLoading || isExtracting"
                    @click="setInputMethod('paste')"
                  >
                    <i class="fas fa-clipboard segment-btn__icon"></i>
                    <span class="segment-btn__label">Colar Texto</span>
                  </button>

                  <button
                    type="button"
                    class="segment-btn segment-btn--neutral"
                    :class="{ 'segment-btn--active': inputMethod === 'file' }"
                    :disabled="isLoading || isExtracting"
                    :title="IMPORT_HINT"
                    @click="setInputMethod('file')"
                  >
                    <i class="fas fa-file-upload segment-btn__icon"></i>
                    <span class="segment-btn__label">Importar arquivo</span>
                  </button>
                </div>

                <div class="visibility-inline visibility-col">
                  <div ref="visibilityDropdownRef" class="visibility-dropdown">
                    <button
                      type="button"
                      class="visibility-trigger"
                      :class="{ active: isVisibilityMenuOpen }"
                      :disabled="isLoading || isSubmitting"
                      aria-labelledby="visibilityLabel"
                      :aria-expanded="isVisibilityMenuOpen"
                      @click.stop="toggleVisibilityMenu"
                    >
                      <i
                        :class="[
 'fas',
                          visibilityMeta(currentAccessMode).icon,
                          'visibility-trigger__icon',
                          visibilityMeta(currentAccessMode).colorClass,
                        ]"
                      ></i>
                      <span class="visibility-trigger__label">{{
                        visibilityMeta(currentAccessMode).label
                      }}</span>
                      <i
                        class="fas fa-chevron-down visibility-trigger__chevron"
                        :class="{ open: isVisibilityMenuOpen }"
                      ></i>
                    </button>

                    <div v-if="isVisibilityMenuOpen" class="visibility-menu">
                      <ul>
                        <li
                          v-for="opt in VISIBILITY_OPTIONS"
                          :key="opt.value"
                          class="visibility-option"
                          :class="[
 opt.colorClass,
                            {
                              'visibility-option--current':
                                currentAccessMode === opt.value,
                            },
                          ]"
                          :title="opt.hint"
                          @click="handleVisibilityOption(opt.value)"
                        >
                          <span class="visibility-option__label">
                            <i
                              :class="[
 'fas',
                                opt.icon,
                                'visibility-option__icon',
                              ]"
                            ></i>
                            {{ opt.label }}
                          </span>
                          <i
                            v-if="currentAccessMode === opt.value"
                            class="fas fa-check visibility-option__check"
                          ></i>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Só no modo personalizado: é o único em que ainda há algo a
                       configurar depois de escolher. -->
                  <button
                    v-if="currentAccessMode === 'custom'"
                    type="button"
                    class="access-hint__link"
                    :disabled="isLoading || isSubmitting"
                    @click="openPermissionModal"
                  >
                    Editar permissões
                  </button>
                </div>
              </div>
            </div>

            <!--
              O input fica fora dos dois ramos de modo porque quem o aciona é o
              botão do segmento, que também está fora deles. Escondido: a caixa
              que o navegador desenha não combina com o resto do formulário.

              A linha de estado que existia aqui SAIU. Ela ficava em branco quase
              o tempo todo — só tinha o que dizer durante a extração — e essa
              faixa vazia era o vão entre "Forma de entrada" e o rótulo do
              editor. O aviso de extração passou para dentro do próprio rótulo,
              que já existe em todo estado: some o vão, e continua não havendo
              solavanco, porque nada nasce nem morre no fluxo.
            -->
            <input
              ref="fileInputRef"
              id="dropzone-file"
              type="file"
              class="hidden"
              :disabled="isExtracting"
              @change="handleFileChange"
              @cancel="handleFilePickerCancel"
              accept=".txt,.pdf,.epub,.doc,.docx"
            />

            <div
              v-if="inputMethod === 'paste'"
              class="form-group form-group--editor"
            >
              <!-- A MESMA linha existe nos dois modos, acima do editor: rótulo à
                   esquerda, expandir à direita. É o que faz colar e carregar
                   lerem como o mesmo campo com duas origens, em vez de duas
                   telas diferentes. -->
              <div class="body-label-row">
                <label class="form-label">Conteúdo</label>

                <!-- 🔹 NOVO: botão expandir/colapsar (espelho do AddNoteForm) -->
                <button
                  type="button"
                  class="ct-expand-btn-inline"
                  :disabled="isLoading || isSubmitting"
                  @click="handleToggleBodyExpand"
                  :aria-label="
                    isBodyExpanded ? 'Colapsar editor' : 'Expandir editor'
                  "
                  :title="
                    isBodyExpanded ? 'Colapsar editor' : 'Expandir editor'
                  "
                >
                  <i
                    v-if="!isBodyExpanded"
                    class="fa fa-expand"
                    aria-hidden="true"
                  ></i>
                  <i v-else class="fa fa-compress" aria-hidden="true"></i>
                </button>
              </div>

              <div class="editor-container">
                <!--
                  Editor rich text: o conteúdo do texto é a ÚNICA coisa no sistema
                  que suporta HTML de ponta a ponta. Os demais campos continuam no
                  CustomTextarea — ali as colunas são texto plano e a exibição
                  também, então formatar seria perda garantida.

                  unwrapPastedLineBreaks preserva o comportamento que este campo já
                  tinha: desfazer quebra dura de PDF ao colar texto puro.
                -->
                <!--
                  Altura FIXA, a mesma em todos os modos: piso e teto iguais
                  fecham a caixa e entregam o excesso à rolagem interna do
                  `.rte-content`. Antes ela vinha da cadeia flex, e por isso mudava
                  conforme o que mais estivesse na tela — colar e carregar davam
                  caixas de tamanhos diferentes para o mesmo campo.

                  Expandido é a exceção, e é a razão de o botão existir: ali o
                  piso volta a zero e o teto sai, e a altura passa a vir da cadeia
                  flex de novo, que é o que deixa o editor tomar o painel.
                -->
                <RichTextEditor
                  :disabled="isExtracting"
                  v-model="obj.pastedContent"
                  @update:modelValue="fieldErrors.pastedContent = null"
                  class="editor-textarea"
                  :class="{ 'textarea-error': fieldErrors.pastedContent }"
                  :unwrapPastedLineBreaks="true"
                  :min-height="isBodyExpanded ? '0' : EDITOR_HEIGHT"
                  :max-height="isBodyExpanded ? null : EDITOR_HEIGHT"
                  placeholder="Cole ou digite o conteúdo do texto…"
                />
              </div>
              <span v-if="fieldErrors.pastedContent" class="error-msg">{{
                fieldErrors.pastedContent
              }}</span>
            </div>

            <!--
              Sem zona de arrasto: escolher "Importar arquivo" já abre o
              seletor nativo (ver setInputMethod). O input e a linha de estado
              subiram para fora deste ramo — ver o comentário lá em cima.
            -->
            <div v-else class="form-group form-group--file">
              <span v-if="fieldErrors.file" class="error-msg">{{
                fieldErrors.file
              }}</span>

              <!-- Conteúdo do arquivo, já convertido para HTML — mesmo editor do
                   modo "Colar Texto", para o usuário conferir e ajustar antes de
                   salvar.

                   Sempre montado, e não só depois de carregar: com `v-if` a caixa
                   nascia e sumia, e o formulário saltava de altura a cada arquivo
                   escolhido. Vazia, ela mostra onde o conteúdo vai aparecer — e
                   nada impede digitar ali antes, se for o caso.

                   O rótulo é que muda de papel conforme o estado. -->
              <div class="loaded-file-preview">
                <!-- A MESMA linha do modo colar: rótulo à esquerda, expandir à
                     direita. O rótulo muda de palavras conforme o estado, mas o
                     arranjo não muda — é o que faz os dois modos lerem como o
                     mesmo campo. -->
                <div class="body-label-row">
                  <!-- Um rótulo, três estados. O nome do arquivo entra entre
                       parênteses porque qualifica o conteúdo que está na caixa
                       logo abaixo; o aviso de extração ocupa o mesmo lugar em vez
                       de uma linha própria, que ficaria vazia o resto do tempo.
                       `:title` carrega o nome inteiro quando as reticências
                       cortam. -->
                  <label
                    for="fileContentEditor"
                    class="form-label body-label-row__label"
                    :class="{ 'body-label-row__label--busy': isExtracting }"
                    :title="fileName || undefined"
                  >
                    <i
                      v-if="isExtracting"
                      class="fas fa-spinner fa-spin body-label-row__spinner"
                    ></i>
                    <template v-if="isExtracting"
                      >Extraindo conteúdo do {{ extractingExtension }}. Isso pode
                      levar vários segundos...</template
                    >
                    <template v-else-if="fileName"
                      >Conteúdo carregado
                      (<span class="body-label-row__file">{{ fileName }}</span
                      >)</template
                    >
                    <template v-else>Conteúdo</template>
                  </label>

                  <div class="body-label-row__actions">
                    <button
                      type="button"
                      class="ct-expand-btn-inline"
                      :disabled="isLoading || isSubmitting"
                      @click="handleToggleBodyExpand"
                      :aria-label="
                        isBodyExpanded ? 'Colapsar editor' : 'Expandir editor'
                      "
                      :title="
                        isBodyExpanded ? 'Colapsar editor' : 'Expandir editor'
                      "
                    >
                      <i
                        v-if="!isBodyExpanded"
                        class="fa fa-expand"
                        aria-hidden="true"
                      ></i>
                      <i v-else class="fa fa-compress" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>

                <!-- Mesma altura fixa do modo colar — ver EDITOR_HEIGHT. -->
                <RichTextEditor
                  :disabled="isExtracting"
                  v-model="obj.pastedContent"
                  @update:modelValue="fieldErrors.pastedContent = null"
                  :unwrapPastedLineBreaks="true"
                  :min-height="isBodyExpanded ? '0' : EDITOR_HEIGHT"
                  :max-height="isBodyExpanded ? null : EDITOR_HEIGHT"
                  placeholder="Conteúdo do arquivo…"
                />
              </div>
            </div>

            <!-- A visibilidade subiu para a linha do campo Conteúdo; aqui resta
                 o acesso ao resto do cadastro. -->
            <div class="quick-row" v-show="!isBodyExpanded">
              <!-- 3. O resto do cadastro, discreto e recolhido -->
              <button
                type="button"
                class="details-toggle"
                :class="{ 'details-toggle--open': showDetails }"
                :disabled="isLoading || isSubmitting"
                :aria-expanded="showDetails"
                @click="toggleDetails"
              >
                <i
                  class="fas fa-chevron-down details-toggle__chevron"
                  aria-hidden="true"
                ></i>
                <span>Adicionar detalhes</span>
              </button>
            </div>

            <div
              ref="detailsPanelRef"
              v-show="showDetails && !isBodyExpanded"
              class="details-panel"
            >
              <p class="details-panel__hint">
                <i class="fas fa-info-circle"></i>
                Nada aqui é obrigatório agora — dá para completar depois, na
                edição do texto.
              </p>

              <div class="form-group-toggle">
                <label for="active" class="form-label">Texto Ativo</label>
                <label class="toggle-switch">
                  <input
                    v-model="obj.active"
                    type="checkbox"
                    id="active"
                    :disabled="isLoading"
                  />
                  <span class="slider"></span>
                </label>
              </div>

              <div class="form-group">
                <label for="subtitle" class="form-label"
                  >Subtítulo (opcional)</label
                >
                <input
                  v-model="obj.subtitle"
                  @input="fieldErrors.subtitle = null"
                  @blur="obj.subtitle = toTitleCase(obj.subtitle)"
                  type="text"
                  id="subtitle"
                  class="input-base"
                  :class="{ 'input-error': fieldErrors.subtitle }"
                  :disabled="isLoading"
                  placeholder="Digite o subtítulo"
                />
                <span v-if="fieldErrors.subtitle" class="error-msg">{{
                  fieldErrors.subtitle
                }}</span>
              </div>

              <div class="form-group">
                <label for="source" class="form-label">Fonte (opcional)</label>
                <input
                  v-model="obj.source"
                  @input="fieldErrors.source = null"
                  @blur="obj.source = toTitleCase(obj.source)"
                  type="text"
                  id="source"
                  class="input-base"
                  :class="{ 'input-error': fieldErrors.source }"
                  :disabled="isLoading"
                  placeholder="Digite a fonte do texto"
                />
                <span v-if="fieldErrors.source" class="error-msg">{{
                  fieldErrors.source
                }}</span>
              </div>

              <div class="form-group">
                <label class="form-label">Autor (opcional)</label>
                <Multiselect
                  ref="authorFocusRef"
                  v-model="selectedAuthor"
                  :options="authorList"
                  :class="{ 'ms-error': fieldErrors.authorId }"
                  placeholder="Selecione ou crie um autor"
                  label="label"
                  track-by="value"
                  :allow-empty="true"
                  :taggable="true"
                  :multiple="true"
                  open-direction="above"
                  tag-placeholder="Tecle enter ou clique aqui para criar um autor"
                  select-label="Tecle enter para selecionar"
                  :close-on-select="true"
                  :clear-on-select="true"
                  :disabled="isLoading"
                  @tag="addNewAuthor"
                  @search-change="onSearch"
                >
                  <template #tag="{ option, remove }">
                    <span class="ms-chip">
                      <button
                        type="button"
                        class="ms-chip-link"
                        @click.stop="openAuthorCard(option)"
                      >
                        {{ option.label }}
                      </button>

                      <span v-if="option.isSys" class="sys-badge">SYS</span>

                      <button
                        type="button"
                        class="ms-chip-remove"
                        @click.stop="remove(option)"
                        aria-label="Remover"
                      >
                        ×
                      </button>
                    </span>
                  </template>

                  <template #option="{ option }">
                    <div class="ms-option">
                      <span class="ms-label">{{ option.label }}</span>
                      <span v-if="option.isSys" class="sys-badge">SYS</span>
                    </div>
                  </template>
                </Multiselect>
                <span v-if="fieldErrors.authorId" class="error-msg">{{
                  fieldErrors.authorId
                }}</span>
              </div>

              <div class="form-group">
                <label class="form-label">Gênero (opcional)</label>
                <Multiselect
                  ref="categoryFocusRef"
                  v-model="selectedCategory"
                  :options="categoryList"
                  :class="{ 'ms-error': fieldErrors.categoryId }"
                  placeholder="Selecione ou crie um gênero"
                  label="label"
                  track-by="value"
                  :allow-empty="true"
                  :taggable="true"
                  :multiple="true"
                  open-direction="above"
                  tag-placeholder="Tecle enter ou clique aqui para criar um gênero"
                  select-label="Tecle enter para selecionar"
                  :close-on-select="true"
                  :clear-on-select="true"
                  :disabled="isLoading"
                  @tag="addNewCategory"
                >
                  <template #tag="{ option, remove }">
                    <span class="ms-chip">
                      <button
                        type="button"
                        class="ms-chip-link"
                        @click.stop="openCategoryCard(option)"
                      >
                        {{ option.label }}
                      </button>

                      <span v-if="option.isSys" class="sys-badge">SYS</span>

                      <button
                        type="button"
                        class="ms-chip-remove"
                        @click.stop="remove(option)"
                        aria-label="Remover"
                      >
                        ×
                      </button>
                    </span>
                  </template>

                  <template #option="{ option }">
                    <div class="ms-option">
                      <span class="ms-label">{{ option.label }}</span>
                      <span v-if="option.isSys" class="sys-badge">SYS</span>
                    </div>
                  </template>
                </Multiselect>
                <span v-if="fieldErrors.categoryId" class="error-msg">{{
                  fieldErrors.categoryId
                }}</span>
              </div>

              <div class="form-group">
                <label class="form-label">Assuntos (opcional)</label>
                <Multiselect
                  ref="topicFocusRef"
                  v-model="selectedTopics"
                  :options="topicList"
                  :class="{ 'ms-error': fieldErrors.topicIds }"
                  placeholder="Selecione ou crie assuntos"
                  label="label"
                  track-by="value"
                  :multiple="true"
                  open-direction="above"
                  tag-placeholder="Tecle enter ou clique aqui para criar um assunto"
                  select-label="Tecle enter para selecionar"
                  deselect-label="Tecle enter para remover"
                  selected-label="Selecionado"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :taggable="true"
                  :disabled="isLoading"
                  @tag="addNewTopic"
                >
                  <template #tag="{ option, remove }">
                    <span class="ms-chip">
                      <button
                        type="button"
                        class="ms-chip-link"
                        @click.stop="openTopicCard(option)"
                      >
                        {{ option.label }}
                      </button>

                      <span v-if="option.isSys" class="sys-badge">SYS</span>

                      <button
                        type="button"
                        class="ms-chip-remove"
                        @click.stop="remove(option)"
                        aria-label="Remover"
                      >
                        ×
                      </button>
                    </span>
                  </template>

                  <template #option="{ option }">
                    <div class="ms-option">
                      <span class="ms-label">{{ option.label }}</span>
                      <span v-if="option.isSys" class="sys-badge">SYS</span>
                    </div>
                  </template>
                </Multiselect>
                <span v-if="fieldErrors.topicIds" class="error-msg">{{
                  fieldErrors.topicIds
                }}</span>
              </div>

              <!-- Capa (opcional). Sem imagem, o card desenha a capa gerada —
                   por isso não há estado vazio a resolver aqui. -->
              <div class="form-group">
                <label class="form-label">Capa (opcional)</label>
                <div class="cover-picker">
                  <div class="cover-preview">
                    <img
                      v-if="coverPreview"
                      :src="coverPreview"
                      alt="Pré-visualização da capa"
                    />
                    <span v-else class="cover-preview__empty">
                      <i class="fas fa-image"></i>
                    </span>
                  </div>

                  <div class="cover-picker__actions">
                    <label class="btn-cover" :class="{ 'btn-cover--busy': isProcessingCover }">
                      <i class="fas fa-upload"></i>
                      <span>{{
                        isProcessingCover
                          ? "Processando…"
                          : coverPreview
                          ? "Trocar imagem"
                          : "Escolher imagem"
                      }}</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        class="cover-input"
                        :disabled="isLoading || isProcessingCover"
                        @change="onCoverChange"
                      />
                    </label>

                    <button
                      v-if="coverPreview"
                      type="button"
                      class="btn-cover btn-cover--ghost"
                      :disabled="isLoading"
                      @click="clearCover"
                    >
                      <i class="fas fa-times"></i>
                      <span>Remover</span>
                    </button>

                    <p class="cover-hint">
                      A imagem é recortada em 3:4 e reduzida a 400&nbsp;px antes
                      do envio.
                    </p>
                  </div>
                </div>
                <span v-if="coverError" class="error-msg">{{ coverError }}</span>
              </div>

              <!-- Último campo do painel: é o mais alto e o único de texto
                   livre longo — acima dos demais, empurraria a catalogação
                   inteira para fora da vista ao ser preenchido. -->
              <div class="form-group">
                <label for="synopsis" class="form-label"
                  >Sinopse (opcional)</label
                >
                <!-- Textarea, não input: a sinopse é um parágrafo. O maxlength
                     é o tamanho da coluna no banco — barrar aqui evita a viagem
                     até a API só para receber erro de tamanho. Sem toTitleCase
                     no blur: é frase, não nome catalogado. -->
                <textarea
                  v-model="obj.synopsis"
                  @input="fieldErrors.synopsis = null"
                  id="synopsis"
                  class="input-base synopsis-input"
                  :class="{ 'input-error': fieldErrors.synopsis }"
                  :disabled="isLoading"
                  rows="10"
                  :maxlength="SYNOPSIS_MAX_LENGTH"
                  placeholder="Um resumo curto, exibido no card do texto"
                ></textarea>
                <div class="synopsis-footer">
                  <span v-if="fieldErrors.synopsis" class="error-msg">{{
                    fieldErrors.synopsis
                  }}</span>
                  <span v-else></span>
                  <!-- Só acende perto do teto: um contador sempre destacado
                       sugere que o limite é assunto do usuário desde o
                       primeiro caractere, e ele não é. -->
                  <span
                    class="synopsis-counter"
                    :class="{ 'synopsis-counter--near': synopsisNearLimit }"
                  >
                    {{ synopsisLength }}/{{ SYNOPSIS_MAX_LENGTH }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Confirmação: quem só queria começar a trabalhar com o texto
               conclui aqui; quem quer catalogar agora segue para a edição. -->
          <div v-else class="success-state">
            <div class="success-state__icon">
              <i class="fas fa-check" aria-hidden="true"></i>
            </div>
            <h4 class="success-state__title">Texto adicionado</h4>
            <p class="success-state__text">
              “{{ createdText.title }}” já está na sua lista de textos.
            </p>
            <template v-if="createdText.pending.length">
              <p class="success-state__pending-label">Ainda em aberto</p>

              <div class="success-pending">
                <button
                  v-for="item in createdText.pending"
                  :key="item.field"
                  type="button"
                  class="pending-chip"
                  :title="`Preencher ${item.label} agora`"
                  @click="completeDetails(item.field)"
                >
                  <i :class="['fas', item.icon, 'pending-chip__icon']"></i>
                  <span class="pending-chip__label">{{ item.label }}</span>
                  <i
                    class="fas fa-arrow-right pending-chip__go"
                    aria-hidden="true"
                  ></i>
                </button>
              </div>

              <p class="success-state__hint">
                Clique em um deles para preencher agora, ou conclua e deixe para
                depois.
              </p>
            </template>

            <p v-else class="success-state__hint">
              Catalogação completa — nada ficou em aberto.
            </p>
          </div>

        </div>

        <div class="modal-footer">
          <!-- Uma ação só: completar é papel dos itens em aberto acima, que
               levam ao campo certo em vez de a uma tela genérica. -->
          <template v-if="createdText">
            <button
              ref="finishFocusRef"
              type="button"
              class="btn btn-primary"
              @click="finishAndClose"
            >
              Concluir
            </button>
          </template>

          <template v-else>
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="isSubmitting"
              @click="closeModal"
            >
              Cancelar
            </button>

            <button
              type="submit"
              class="btn btn-success"
              :disabled="
                isLoading || isSubmitting || isExtracting || isBodyExpanded
              "
            >
              <svg
                v-if="!isSubmitting"
                class="mr-1 -ml-1 w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  clip-rule="evenodd"
                ></path>
              </svg>

              <svg
                v-else
                class="mr-2 -ml-1 w-5 h-5 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>

              <span>{{
                isSubmitting ? "Adicionando..." : "Adicionar Texto"
              }}</span>
            </button>
          </template>
        </div>
      </form>
    </div>
  </div>

  <!-- ✅ Mini-modal de permissões (espelho de AddActivityForm.vue) -->
  <Teleport to="body">
    <transition name="permission-modal-fade">
      <div v-if="showPermissionModal" class="permission-modal-backdrop">
        <div
          ref="permPanelRef"
          class="permission-modal-panel"
          :style="permPanelStyle"
          @click.stop
        >
          <div
            class="permission-modal-header"
            :class="{
 'permission-modal-header--draggable': isPermDraggable,
              'permission-modal-header--dragging': isPermDragging,
            }"
            @pointerdown="onPermHandlePointerDown"
          >
            <h4 class="permission-modal-title">Editar Permissões</h4>
            <button
              type="button"
              class="permission-modal-close"
              @click="cancelPermissionModal"
              aria-label="Fechar Editar Permissões"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </button>
          </div>

          <div class="permission-modal-body">
            <p class="permission-modal-instruction">
              Defina aqui usuários, grupos e permissões personalizadas para este
              texto.
            </p>
            <AddPermissionForm
              ref="permissionFormRef"
              v-model:payload="obj.permissions"
              :key="permissionFormKey"
              :users="userListForChild"
              :groups="groupListForChild"
              :permissions="permissionListForChild"
              entityType="Text"
              :creatorUserName="loggedUser?.userName"
            />
          </div>

          <div class="permission-modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="cancelPermissionModal"
              :disabled="isSubmitting"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-success"
              @click="handleConfirmPermissionModal"
              :disabled="isSubmitting"
            >
              Confirmar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <AddTextLimitModal
    :show="showTextLimitModal"
    :info="textLimitInfo"
    entityType="textos"
    @close="showTextLimitModal = false"
    @open-plans="openPlansFromLimitModal"
  />

  <Teleport to="body">
    <div
      v-if="showPlanCatalogModal"
      class="fixed inset-0 z-[9000] bg-black/20 flex justify-center items-start p-2 sm:p-3 overflow-hidden"
    >
      <div
        class="relative w-full max-w-[1344px] h-[94vh] overflow-y-auto self-start bg-[var(--bg-main)] rounded-2xl shadow-none p-0"
        @click.stop
      >
        <PlanCatalogByName
          class="h-full"
          @close="handlePlanCatalogClose"
          @select="handlePlanSelect"
        />
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="showAddAuthorModal" class="fixed inset-0 z-[10050]">
      <AddAuthorForm
        :initialName="newAuthorName"
        @close="showAddAuthorModal = false"
        @author-added="handleAuthorAdded"
      />
    </div>

    <div v-if="showAddCategoryModal" class="fixed inset-0 z-[10050]">
      <AddCategoryForm
        :initialName="newCategoryName"
        @category-added="handleCategoryAdded"
        @close="showAddCategoryModal = false"
      />
    </div>

    <div v-if="showAddTopicModal" class="fixed inset-0 z-[10050]">
      <AddTopicForm
        :initialName="newTopicName"
        @topic-added="handleTopicAdded"
        @close="showAddTopicModal = false"
      />
    </div>
  </Teleport>

  <TopicCardModal
    :show="showTopicCardModal"
    :topicId="selectedTopicIdForModal"
    @close="closeTopicCard"
  />

  <AuthorCardModal
    :show="showAuthorCardModal"
    :authorId="selectedAuthorIdForModal"
    @close="closeAuthorCard"
  />

  <CategoryCardModal
    :show="showCategoryCardModal"
    :categoryId="selectedCategoryIdForModal"
    @close="closeCategoryCard"
  />

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

/* =========================
   ERROS INLINE E VALIDAÇÃO
========================= */
.input-error {
  border-color: var(--danger) !important;
  box-shadow: var(--focus-ring-danger) !important;
}

.ms-error :deep(.multiselect__tags) {
  border-color: var(--danger) !important;
  box-shadow: var(--focus-ring-danger) !important;
}

.textarea-error :deep(textarea),
.textarea-error :deep(.ql-container) {
  border-color: var(--danger) !important;
  box-shadow: var(--focus-ring-danger) !important;
}

.error-msg {
  color: var(--danger);
  font-size: 0.8rem;
  margin-top: 0.3rem;
  display: block;
}

/* O `.input-base` global (main.css) fixa `height: 34px`, e altura fixa em
   textarea ANULA o atributo `rows` — era isso que deixava a sinopse com uma
   linha só. `height: auto` devolve o comando ao `rows`.

   A regra escopada vence a global por especificidade: o Vue a compila como
   `.synopsis-input[data-v-hash]`, com um seletor a mais que `.input-base`.
   Por isso a correção mora aqui e não em main.css, que serve todos os inputs
   do aplicativo. */
.synopsis-input {
  height: auto;
  /* Só na vertical: arrastar na horizontal estouraria a largura do painel. */
  resize: vertical;
}

/* Erro à esquerda, contador à direita, na mesma linha abaixo da textarea.
   O <span> vazio no lugar do erro mantém o contador encostado à direita
   mesmo sem erro — `justify-content: space-between` com um filho só o
   jogaria para a esquerda. */
.synopsis-footer {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.synopsis-counter {
  flex: 0 0 auto;
  margin-top: 0.3rem;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  color: var(--text-subtle);
}

.synopsis-counter--near {
  color: #b45309;
  font-weight: 600;
}

.sys-badge {
  margin-left: 8px;
}

/* Alinhamento do conteúdo no multiselect */
.ms-option,
.ms-single {
  display: inline-flex;
  align-items: center;
  gap: 0;
}

.ms-label {
  line-height: 1.2;
}

.modal-panel {
  height: calc(100vh - 20px - 2rem);
  max-height: calc(100vh - 20px - 2rem);
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.modal-header-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

/* Substitui o DetailedStepperText: a mesma informação (passo atual / total e
   o título do passo), só que compacta no cabeçalho em vez de uma barra própria. */
.modal-step-indicator {
  font-size: 0.75rem;
  color: var(--text-light);
  margin: 0;
}

.modal-close-btn {
  width: 32px;
}

.modal-footer {
  bottom: 0;
}

/* =========================
   STEP CONTENT
========================= */
/* `gap` de 0.85rem, não 1.25rem: são cinco intervalos entre os grupos do step,
   então cada 0.1rem aqui vale meia linha no fim. O alvo é o formulário caber
   inteiro — "Adicionar detalhes" incluído — mesmo com o arquivo carregado. */
.step-content {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-height: 0;

  /* ✅ Novo: ocupa o que sobra dentro do modal-body */
  flex: 1 1 auto;
}

.form-group-toggle {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

/* =========================
   DROPZONE
========================= */
/* O nome do arquivo destacado dentro do rótulo: mesmo roxo dos interruptores de
   modo do sistema. Ele é o único trecho variável da frase, e a cor é o que o
   separa do texto fixo sem precisar de peso nem de aspas. */
.body-label-row__file {
  color: #4f46e5;
  font-weight: 700;
}

/* Ícone de espera dentro do rótulo do editor, enquanto o PDF é extraído. */
.body-label-row__spinner {
  margin-right: 0.35rem;
  font-size: 0.8rem;
  opacity: 0.8;
}

/* =========================
   TOGGLE
========================= */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: var(--neutral-200);
  transition: 0.25s;
  border-radius: 999px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  top: 3px;
  background-color: var(--surface);
  transition: 0.25s;
  border-radius: 50%;
}

.toggle-switch input:checked + .slider {
  background-color: var(--brand-light);
}

.toggle-switch input:checked + .slider:before {
  transform: translateX(16px);
}

/* =========================
   CHIPS
========================= */
.ms-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 7px;
  border: 0px solid var(--neutral-400);
  background: var(--surface-3);
  font-size: 0.85rem;
  margin: 2px 6px 2px 0;
}

.ms-chip-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0 2px;
  color: var(--text-muted);
}
.ms-chip-remove:hover {
  color: #0f172a;
}

.ms-chip-link {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;

  cursor: pointer;
  color: inherit;
  font: inherit;
  line-height: 1.1;

  text-decoration: none;
}

.ms-chip-link:hover {
  color: var(--link);
  text-decoration: underline;
}

.ms-chip-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
  border-radius: 6px;
}

/* =========================
   EDITOR DE TEXTO (STEP 2)
========================= */
/* Mede o conteúdo: a altura do editor agora é fixa e vem da prop (EDITOR_HEIGHT),
   não da disputa por espaço no painel. Era `flex: 1 1 auto; min-height: 14rem`,
   de quando o editor esticava — mantê-lo esticando deixaria um vão embaixo da
   caixa fixa. */
.form-group--editor {
  flex: 0 0 auto;
  min-height: 0;

  display: flex;
  flex-direction: column;
}

/* ── Expandido: quem rola é o EDITOR, não o corpo do modal ─────────────────

   `overflow: hidden` aqui é a peça que faltava. Enquanto o .modal-body seguia
   `overflow-y: auto`, ele nunca limitava a altura dos filhos: o editor crescia
   com o conteúdo, o corpo do modal rolava no lugar dele, e a caixa expandida
   aparecia SEM barra de rolagem própria — o `overflow-y: auto` do .rte-content
   nunca chegava a disparar porque nada o obrigava a caber.

   Fechado o corpo, a altura desce pela cadeia flex e o editor volta a ter teto.
   É o mesmo arranjo do AddActivityForm. */
.modal-body--expanded {
  overflow: hidden;
}

/* Expandido é a exceção: aí o editor volta a esticar, que é o que o botão
   promete. A prop devolve piso zero e teto nenhum; aqui a cadeia flex volta a
   poder crescer. */
.step-content--expanded .form-group--editor {
  flex: 1 1 auto;
}

/* A mesma cadeia no modo arquivo: o grupo é `flex: 0 0 auto` em repouso (mede o
   conteúdo), e precisa voltar a esticar quando expandido, ou o editor da prévia
   não recebe altura nenhuma para preencher. */
.step-content--expanded .form-group--file,
.step-content--expanded .loaded-file-preview {
  flex: 1 1 auto;
  min-height: 0;
}

.step-content--expanded .loaded-file-preview :deep(.rte) {
  flex: 1 1 auto;
  min-height: 0;
}

/* Prévia editável do conteúdo carregado no modo "Importar arquivo" — sem o
   flex-fill do .form-group--editor porque este bloco convive com a linha de
   estado do arquivo
   acima, então usa a altura padrão do RichTextEditor em vez de ocupar todo o
   painel do step. */
/* Sem `margin-top`, e com o mesmo `gap` do `.form-group`: é o que põe o rótulo
   "Conteúdo" na MESMA altura nos dois modos. O recuo de 0.25rem que havia aqui
   era justamente o que fazia ele nascer alguns pixels abaixo do rótulo do modo
   colar, onde a linha é o primeiro filho do grupo. */
.loaded-file-preview {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-top: 0;
}

/* ── Modo arquivo ───────────────────────────────────────────────────────────

   `flex: 0 0 auto`: o grupo mede o conteúdo, e nada mais tenta esticá-lo.

   Isto era `flex: 1 1 auto; min-height: 14rem`, de quando a zona de arrasto
   precisava ocupar o painel para não deixar buraco. Com ela fora, esticar não
   tem mais para quê — e o `flex-shrink: 1` que vinha junto era o que espremia o
   grupo abaixo do conteúdo e fazia o editor transbordar por cima da linha de
   visibilidade. Quem rola, se precisar, é o .modal-body. */
.form-group--file {
  flex: 0 0 auto;
  min-height: 0;

  display: flex;
  flex-direction: column;
}

.editor-container {
  flex: 1 1 auto;
  min-height: 0;

  display: flex;
  flex-direction: column;
}

.editor-textarea {
  flex: 1 1 auto;
  min-height: 0;
}

/* 🔹 NOVO: faz o <textarea> interno do CustomTextarea preencher todo o espaço
   do editor (elimina o vão vazio entre a textarea e o controle de acesso, e
   também sustenta o modo expandido). */
.editor-textarea :deep(.custom-textarea) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  resize: none;
}

/* 🔹 NOVO: no modo expandido o editor ocupa todo o painel do step 2 */
.step-content--expanded {
  overflow: hidden;
}

/* 🔹 NOVO: linha do label + botão de expandir (espelho do AddNoteForm) */
/* Rótulo à esquerda, botão de expandir encostado à direita. */
.body-label-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;
}

/* Recolhido não há rótulo aqui — o editor já vem sob o "Conteúdo" do grupo do
   topo —, e `space-between` com um filho só o jogaria para a esquerda. */
.body-label-row--end {
  justify-content: flex-end;
}

/* O nome do arquivo entra no rótulo e pode ser longo: ele corta com reticências
   em vez de empurrar os botões para fora da linha. */
.body-label-row__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* O aviso de extração é uma frase, não um nome: ele quebra em vez de ser cortado
   pelas reticências, que engoliriam justamente a parte que pede paciência. */
.body-label-row__label--busy {
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
}

.body-label-row__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
}

/* 🔹 NOVO: botão expandir/colapsar (mesmo visual do AddNoteForm) */
.ct-expand-btn-inline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: none;
  background: var(--neutral-200);
  color: var(--text-dark);
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.18s ease-in-out;
  flex-shrink: 0;
}
.ct-expand-btn-inline:hover:not(:disabled) {
  background: var(--neutral-400);
  transform: translateY(-1px);
}
.ct-expand-btn-inline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.access-segment {
  display: flex;
  gap: 0.6rem;
  margin-top: 0.25rem;
}

/* Ocupa a largura inteira da coluna: com flex: 1 (abaixo) os botões ficam do
   mesmo tamanho e esticam até o rótulo caber em uma linha só. */
.access-segment--sm {
  width: 100%;
}

/* ── Conteúdo e visibilidade como duas colunas ──────────────────────────────
   Rótulos numa linha, controles na seguinte. As duas linhas usam a MESMA
   largura de coluna à direita, e é isso que faz o rótulo "Visibilidade" cair
   exatamente sobre o gatilho.

   A largura sai do rótulo mais longo do menu, "Personalizado": abaixo disso o
   gatilho mudaria de tamanho ao trocar de modo, e a linha inteira dançaria a
   cada escolha. */
.content-label-row,
.content-method-row {
  /* O gatilho: largura do rótulo mais longo do menu, "Personalizado". */
  --visibility-trigger-w: 11.5rem;
  /* Faixa RESERVADA à direita do gatilho para o "Editar permissões". Fica
     reservada mesmo quando o link não existe (modos público e privado): sem
     isso o gatilho saltaria de lugar a cada troca de modo. */
  --visibility-actions-w: 9rem;
  --visibility-gap: 0.5rem;
  --visibility-col: calc(
    var(--visibility-trigger-w) + var(--visibility-gap) +
      var(--visibility-actions-w)
  );

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.content-label-row {
  align-items: baseline;
}

/* O rótulo da direita não é `<label for>` porque o gatilho é um botão, não um
   campo: quem faz a associação é o `aria-labelledby` do botão. */
.content-label-row .form-label {
  margin: 0;
}

.visibility-col {
  flex: 0 0 auto;
  width: var(--visibility-col);
}

/* Anula o `width: 100%` da regra base: aqui o segmento divide a linha, e largura
   cheia empurraria a visibilidade para a linha de baixo sempre. `flex: 1 1 auto`
   mantém o que aquela regra queria — os botões esticando até o rótulo caber em
   uma linha só — dentro do espaço que sobra. */
.content-method-row .access-segment--sm {
  width: auto;
  flex: 1 1 auto;
  min-width: 0;
  /* A margem existia para descolar do rótulo logo acima; agora quem separa é o
     `gap` da coluna, e mantê-la desalinharia o segmento do gatilho ao lado. */
  margin-top: 0;
}

/* Linha: gatilho à esquerda da coluna, "Editar permissões" na faixa reservada à
   direita dele. Como a coluna inteira é mais larga que o gatilho, ela começa
   mais à esquerda — e o rótulo "Visibilidade", que tem a mesma largura de
   coluna e alinha à esquerda, cai exatamente sobre o gatilho. */
.content-method-row .visibility-inline {
  flex-direction: row;
  align-items: center;
  gap: var(--visibility-gap);
}

.content-method-row .visibility-dropdown {
  flex: 0 0 auto;
  width: var(--visibility-trigger-w);
}

/* Ocupa a faixa reservada e quebra em duas linhas se precisar, em vez de
   empurrar o gatilho. */
.content-method-row .access-hint__link {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  line-height: 1.2;
}

/* `space-between` afasta o chevron do rótulo e o encosta na borda direita —
   num gatilho de largura fixa, agrupá-los no meio deixaria um vazio à direita
   que faria o botão parecer quebrado. */
.content-method-row .visibility-trigger {
  width: 100%;
  justify-content: space-between;
}

/* O rótulo do modo fica com o espaço que sobra entre ícone e chevron, e corta
   com reticências em vez de estourar a coluna. */
.content-method-row .visibility-trigger__label {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* O menu acompanha a coluna: mais estreito que o gatilho pareceria desencaixado,
   e é o `min-width` de 200px da regra base que o alargava sozinho. */
.content-method-row .visibility-menu {
  min-width: 100%;
}

.segment-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  padding: 0.6rem 0.5rem;
  border-radius: 12px;
  border: 1.5px solid var(--neutral-200);
  background-color: var(--surface-2);
  cursor: pointer;
  transition: all 0.18s ease;
  color: var(--text-muted);
}

/* Ícone e rótulo lado a lado em vez de empilhados, para caber na altura fixa.

   32px, a MESMA do `.visibility-trigger` ao lado: eles dividem a linha, e 4px de
   diferença bastavam para as duas colunas não assentarem no mesmo eixo. Já foram
   38px, quando o par era o elemento mais pesado da faixa; a redução continua
   valendo, só que agora parando onde o vizinho para. */
.access-segment--sm .segment-btn {
  flex-direction: row;
  height: 32px;
  padding: 0 0.45rem;
  border-radius: 7px;
  gap: 0.3rem;
  font-size: 0.78rem;
  /* Ícone + rótulo centralizados no botão, que é mais largo que o conteúdo. */
  justify-content: center;
}

.segment-btn:hover:not(:disabled):not(.segment-btn--active) {
  border-color: var(--neutral-400);
  background-color: var(--surface-3);
  color: var(--text-dark);
  transform: translateY(-1px);
}

.segment-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.segment-btn__icon {
  font-size: 1rem;
  line-height: 1;
}
.access-segment--sm .segment-btn__icon {
  font-size: 0.78rem;
}

.segment-btn__label {
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1;
}
.access-segment--sm .segment-btn__label {
  font-size: 0.75rem;
  /* A altura do botão é fixa (32px): o rótulo estica na horizontal em vez de
     quebrar em duas linhas. */
  white-space: nowrap;
}

/* Sem semântica de cor própria (público/privado/personalizado) — usado no
   seletor "Método de Entrada", que só alterna entre duas opções neutras. */
.segment-btn--neutral.segment-btn--active {
  border-color: var(--brand);
  background-color: var(--brand-tint);
  color: var(--text-brand);
  box-shadow: 0 2px 8px rgba(155, 132, 255, 0.15);
}

/* ── Visibilidade condensada: "Visibilidade: Público ▾" ──────────────────────
   Num cadastro rápido a visibilidade é uma escolha secundária, com um padrão
   já bom (público). Ela cabe numa linha de texto, não num campo de formulário
   do tamanho do conteúdo. ── */
/* `flex-end`, e não `space-between`: com a visibilidade movida para a linha do
   Conteúdo, sobrou só o "Adicionar detalhes" aqui, e `space-between` com um
   filho só o jogaria para a esquerda. Ele continua onde sempre esteve. */
.quick-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 0.15rem;
}

.visibility-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-dark);
}

.visibility-dropdown {
  position: relative;
}

/* Gatilho do tamanho do próprio rótulo — é uma escolha, não um campo. */
.visibility-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 32px;
  padding: 0 0.6rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--surface);
  color: var(--text-dark);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.visibility-trigger:hover:not(:disabled) {
  border-color: var(--focus-border);
  background-color: #f5f8ff;
}
.visibility-trigger.active {
  border-color: var(--focus-border);
}
.visibility-trigger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.visibility-trigger__icon {
  font-size: 0.85rem;
}

.visibility-trigger__chevron {
  font-size: 0.6rem;
  opacity: 0.7;
  transition: transform 0.2s ease-in-out;
}
.visibility-trigger__chevron.open {
  transform: rotate(180deg);
}

/* Abre para BAIXO, sempre.

   Antes abria para cima (`bottom: calc(100% + 4px)`), para o painel não nascer
   fora da parte visível do .modal-body. O preço era ele subir POR CIMA do editor
   e cobrir o texto que se acabou de revisar — e, com o corpo rolado, parecer que
   o menu escapava junto com o conteúdo.

   Abrindo para baixo o painel fica sempre abaixo do editor, onde nada tapa. O
   recorte que motivava a versão anterior é resolvido no gatilho, que rola o
   painel para dentro da vista ao abrir (ver toggleVisibilityMenu). */
.visibility-menu {
  position: absolute;
  left: 0;
  top: calc(100% + 4px);
  z-index: 100;
  min-width: 200px;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: var(--shadow-dropdown);
}
.visibility-menu ul {
  list-style: none;
  padding: 0.3rem;
  margin: 0;
}

.visibility-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.7rem;
  border-radius: 7px;
  font-size: 0.85rem;
  color: var(--text-dark);
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease-in-out;
}
/* A cor do modo só aparece no hover e na opção atual — no repouso a lista é
   neutra, como a do dropdown de progresso do TextDisplayer. */
.visibility-option:not(:hover):not(.visibility-option--current) {
  background-color: transparent;
  color: var(--text-dark);
}
.visibility-option--current {
  font-weight: 700;
}
.visibility-option__label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.visibility-option__icon {
  width: 0.95rem;
  text-align: center;
  font-size: 0.8rem;
}
.visibility-option__check {
  color: var(--brand);
  font-size: 0.75rem;
}

.visibility--public {
  background-color: var(--success-bg);
  color: var(--success-text);
}
.visibility--private {
  background-color: var(--warning-bg);
  color: var(--warning-text);
}
.visibility--custom {
  background-color: var(--brand-tint);
  color: var(--text-brand);
}

/* Declarado depois dos .visibility--* de propósito: no ícone vale a cor, nunca
   o fundo em pílula. */
.visibility-trigger__icon,
.visibility-option__icon {
  background-color: transparent;
}

/* ── "Adicionar detalhes": tudo que o cadastro mínimo não precisa ────────── */
.details-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.1rem;
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s ease;
}
.details-toggle:hover:not(:disabled) {
  color: var(--text-brand);
}
.details-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.details-toggle__chevron {
  font-size: 0.7rem;
  transition: transform 0.2s ease-in-out;
}
.details-toggle--open .details-toggle__chevron {
  transform: rotate(180deg);
}

.details-panel {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-top: 0.25rem;
  padding-top: 0.9rem;
  /* Respiro depois do último campo (Assuntos): a área rolável termina rente ao
     rodapé, e o padding do .modal-body sozinho não dá folga no fim da rolagem. */
  padding-bottom: 0.9rem;
  border-top: 1px dashed var(--neutral-200);
}

.details-panel__hint {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: var(--text-muted);
}
.details-panel__hint i {
  font-size: 0.75rem;
  flex-shrink: 0;
}

/* O toggle "Texto Ativo" vinha de uma tela onde ficava sozinho no topo; aqui
   ele é só mais um detalhe da lista, sem o recuo negativo de lá. */
.details-panel .form-group-toggle {
  justify-content: flex-start;
  padding: 0;
}

/* ── Confirmação: "Texto adicionado" ─────────────────────────────────────── */
.success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.5rem;
  padding: 2.5rem 1rem;
  flex: 1 1 auto;
}

.success-state__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  margin-bottom: 0.25rem;
  border-radius: 50%;
  background-color: var(--success-bg);
  color: var(--success);
  font-size: 1.4rem;
}

.success-state__title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-dark);
}

.success-state__text {
  font-size: 0.9rem;
  color: var(--text-light);
  max-width: 34rem;
}

.success-state__hint {
  font-size: 0.8rem;
  color: var(--text-muted);
  max-width: 34rem;
}

.success-state__pending-label {
  margin-top: 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--text-subtle);
}

.success-pending {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

/* Cada item é um atalho, não um aviso: leva direto ao campo que falta, com o
   cursor nele. Daí a seta — e o cursor de mão em cima do rótulo. */
.pending-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--neutral-200);
  background: var(--surface-2);
  color: var(--text-light);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    color 0.15s ease, transform 0.05s ease;
}
.pending-chip:hover {
  border-color: var(--brand);
  background-color: var(--brand-tint);
  color: var(--text-brand);
}
.pending-chip:active {
  transform: translateY(1px);
}

.pending-chip__icon {
  font-size: 0.85rem;
}

/* O fa-book-open desenha o livro na metade de cima do quadro e assenta acima da
   linha do rótulo; 1px devolve o alinhamento óptico com os outros dois. */
.pending-chip__icon.fa-book-open {
  transform: translateY(1px);
}

.pending-chip__go {
  font-size: 0.7rem;
  opacity: 0.55;
}

.access-hint__link {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--brand);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.access-hint__link:hover:not(:disabled) {
  color: #7c63f5;
}
.access-hint__link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-access-toggle:hover:not(:disabled) {
  background-color: var(--neutral-300);
  transform: scale(1.05);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.btn-access-toggle:active:not(:disabled) {
  transform: translateY(1px);
}

.btn-access-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

/* =========================
   MINI-MODAL DE PERMISSÕES
========================= */
.permission-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10100;
  background-color: rgba(15, 23, 42, 0.2);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 50px;
  padding-left: 1rem;
  padding-right: 1rem;
}

.permission-modal-panel {
  width: 590px;
  max-width: 100%;
  height: 760px;
  max-height: calc(100vh - 100px);
  background-color: var(--bg-content);
  border-radius: 18px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-modal-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.permission-modal-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-header);
}

/* 🔹 NOVO: cabeçalho do mini-modal como alça de arrasto.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.permission-modal-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.permission-modal-header--dragging {
  cursor: grabbing;
}
/* Os botões do cabeçalho continuam sendo clique, não alça */
.permission-modal-header--draggable button {
  cursor: pointer;
}

.permission-modal-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.permission-modal-close {
  color: var(--text-light);
  background-color: transparent;
  border-radius: 8px;
  padding: 0.375rem;
  transition: background-color 0.2s, color 0.2s;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}
.permission-modal-close:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: var(--text-dark);
}

.permission-modal-body {
  padding: 0.8rem 1rem 0.5rem;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-content);
  min-height: 0;
  overflow: hidden;
}

.permission-modal-instruction {
  font-size: 0.82rem;
  color: var(--text-light);
  margin-bottom: 0.75rem;
  line-height: 1.35;
  display: block;
}

.permission-modal-footer {
  flex: 0 0 auto;
  padding: 0.6rem 1rem 0.75rem;
  border-top: 1px solid var(--border-color);
  background-color: var(--bg-header);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}

:deep(.permission-form-wrapper) {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

:deep(.mt-5) {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 1rem !important;
}

:deep(.table-wrap) {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: auto;
  border-radius: 8px;
  background: var(--bg-content);
  margin-top: 0.5rem;
}

:deep(.table-wrap::-webkit-scrollbar) {
  width: 6px;
}
:deep(.table-wrap::-webkit-scrollbar-thumb) {
  background-color: var(--neutral-400);
  border-radius: 10px;
}

.permission-modal-fade-enter-active,
.permission-modal-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.18s ease;
}
.permission-modal-fade-enter-from,
.permission-modal-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
/* =========================
   LOADING OVERLAY
========================= */





/* ── Seletor de capa ────────────────────────────────────────────────────────
   A miniatura tem a MESMA proporção do card (3:4): o que o usuário vê aqui é
   exatamente o recorte que vai para a estante, e não uma aproximação. */
.cover-picker {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.cover-preview {
  flex: 0 0 auto;
  width: 84px;
  aspect-ratio: 3 / 4;
  border-radius: 6px;
  border: 1px dashed var(--border-color);
  background: var(--surface-2);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cover-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover-preview__empty {
  color: var(--neutral-400);
  font-size: 1.4rem;
}

.cover-picker__actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
  min-width: 0;
}

/* <label> e não <button>: é o rótulo do input de arquivo, que fica escondido —
   é o que permite estilizar sem depender do botão nativo do navegador. */
.btn-cover {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 32px;
  padding: 0 0.7rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: var(--surface);
  color: var(--text-dark);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.btn-cover:hover {
  background: var(--surface-3);
}

.btn-cover--busy {
  opacity: 0.7;
  pointer-events: none;
}

.btn-cover--ghost {
  border-color: var(--danger-bg);
  background: var(--danger-bg);
  color: var(--danger-text);
}

.btn-cover--ghost:hover {
  background: var(--danger-bg);
}

/* O input existe e recebe o clique pelo <label>; escondê-lo com display:none o
   tiraria da navegação por teclado. */
.cover-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.cover-hint {
  margin: 0;
  font-size: 0.72rem;
  color: var(--text-subtle);
}
</style>
